import React, { useState, useEffect, useRef, useCallback } from "react";
import { MagnifierPlus, MagnifierMinus, ArrowRotateLeft, Xmark } from "@gravity-ui/icons";
import { DetectionZonesOverlay } from "../DetectionZonesOverlay/DetectionZonesOverlay";
import "./ZoomModal.css";

/**
 * ZoomModal — полноэкранный просмотр изображения с зумом и панорамированием.
 *
 * Исправление бага закрытия при перетаскивании:
 *   После mouseUp браузер генерирует click. isDragging (state) к этому моменту
 *   уже сброшен в false, поэтому проверка !isDragging срабатывала ложно.
 *   Вместо state используем ref wasDragged: он ставится в true при каждом
 *   mousemove во время drag и сбрасывается только внутри onClick — до того,
 *   как click может сработать снова.
 */
const ZoomModal = ({ src, onClose, zones = [], referenceSrc = "" }) => {
  const [scale, setScale] = useState(1);
  const [pos, setPos]     = useState({ x: 0, y: 0 });
  const [cursor, setCursor] = useState("grab");

  const dragging  = useRef(false);
  const wasDragged = useRef(false);   // ← ключевое исправление
  const last      = useRef({ x: 0, y: 0 });
  const touchState = useRef({ dist: 0 });

  // ── Колесо мыши ──────────────────────────────────────────────────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setScale(s => Math.max(0.3, Math.min(8, s * (1 - e.deltaY * 0.001))));
  }, []);

  // ── Начало перетаскивания ─────────────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragging.current = true;
    setCursor("grabbing");
    last.current = { x: e.clientX, y: e.clientY };
  }, []);

  // ── Глобальные mousemove / mouseup ────────────────────────────────────────
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragging.current) return;
      wasDragged.current = true;          // помечаем, что был реальный drag
      const dx = e.clientX - last.current.x;
      const dy = e.clientY - last.current.y;
      last.current = { x: e.clientX, y: e.clientY };
      setPos(p => ({ x: p.x + dx, y: p.y + dy }));
    };

    const handleMouseUp = () => {
      dragging.current = false;
      setCursor("grab");
      // wasDragged НЕ сбрасываем здесь — сброс происходит в onClick
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup",   handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup",   handleMouseUp);
    };
  }, []);

  // ── Touch (мобильные) ─────────────────────────────────────────────────────
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      dragging.current = true;
      last.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      touchState.current.dist = Math.sqrt(dx * dx + dy * dy);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 1 && dragging.current) {
      wasDragged.current = true;
      const dx = e.touches[0].clientX - last.current.x;
      const dy = e.touches[0].clientY - last.current.y;
      last.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setPos(p => ({ x: p.x + dx, y: p.y + dy }));
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const newDist = Math.sqrt(dx * dx + dy * dy);
      const ratio   = newDist / (touchState.current.dist || newDist);
      touchState.current.dist = newDist;
      setScale(s => Math.max(0.3, Math.min(8, s * ratio)));
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    dragging.current = false;
  }, []);

  // ── Escape ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ── Клик по подложке — закрываем только если drag не было ────────────────
  const handleOverlayClick = useCallback(() => {
    if (wasDragged.current) {
      wasDragged.current = false;   // сбрасываем флаг
      return;
    }
    onClose();
  }, [onClose]);

  return (
    <div className="zm-overlay" onWheel={handleWheel}>
      {/* Тулбар */}
      <div className="zm-toolbar">
        <button className="zm-btn" onClick={() => setScale(s => Math.min(s + 0.5, 8))} title="Приблизить"><MagnifierPlus width={16} height={16} /></button>
        <button className="zm-btn" onClick={() => setScale(s => Math.max(s - 0.5, 0.3))} title="Отдалить"><MagnifierMinus width={16} height={16} /></button>
        <button className="zm-btn" onClick={() => { setScale(1); setPos({ x: 0, y: 0 }); }} title="Сбросить"><ArrowRotateLeft width={16} height={16} /></button>
        <span className="zm-scale">{Math.round(scale * 100)}%</span>
        <button className="zm-btn zm-btn--close" onClick={onClose} title="Закрыть"><Xmark width={16} height={16} /></button>
      </div>

      {/* Область изображения */}
      <div
        className="zm-image-wrap"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleOverlayClick}
        style={{ cursor }}
      >
        {/* Зоны (ТЗ V5 №6) трансформируются вместе с кадром. */}
        <div
          className="zm-transform"
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
            transformOrigin: "center center",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          <DetectionZonesOverlay src={src} alt="Увеличенный кадр" zones={zones} referenceSrc={referenceSrc} />
        </div>
      </div>
    </div>
  );
};

export default ZoomModal;
